import { Service } from "@angular/core";
import saveAs from "file-saver";

export type CsvEncoding = "utf-16le" | "utf-8";

export interface CsvSaveOptions {
  fileName: string;
  headers?: string[];
  rows?: string[][]; // Preferred: structured rows
  lines?: string[]; // Alternative: prebuilt lines
  delimiter?: string; // Default: tab ("\t")
  encoding?: CsvEncoding; // Default: "utf-16le"
}

@Service()
export class CsvExportService {
  public saveCsv(options: CsvSaveOptions): void {
    const delimiter = options.delimiter ?? "\t";
    const encoding: CsvEncoding = options.encoding ?? "utf-16le";

    const content = this._buildContent(options, delimiter);

    if (encoding === "utf-16le") {
      const utf16Encoded = new Uint8Array([0xff, 0xfe, ...this._stringToUTF16(content)]);
      const blob = new Blob([utf16Encoded], { type: "text/csv;charset=utf-16le;" });
      saveAs(blob, this._ensureCsvExtension(options.fileName));
      return;
    }

    // UTF-8 with BOM for proper opening in Excel
    const BOM = "\uFEFF";
    const csvContent = BOM + content;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, this._ensureCsvExtension(options.fileName));
  }

  public escape(value: unknown, delimiter: string): string {
    const str = value == null ? "" : String(value);
    const needsQuoting = new RegExp(`["${this._escapeForRegExp(delimiter)}\n\r]`).test(str);
    const escaped = str.replace(/"/g, '""');
    return needsQuoting ? `"${escaped}"` : escaped;
  }

  private _buildContent(options: CsvSaveOptions, delimiter: string): string {
    if (options.lines && options.lines.length > 0) {
      return this._joinCRLF(options.lines);
    }

    const lines: string[] = [];

    if (options.headers && options.headers.length > 0) {
      lines.push(options.headers.map((h) => this.escape(h, delimiter)).join(delimiter));
    }

    (options.rows ?? []).forEach((row) => {
      const escaped = row.map((cell) => this.escape(cell, delimiter));
      lines.push(escaped.join(delimiter));
    });

    return this._joinCRLF(lines);
  }

  private _joinCRLF(lines: string[]): string {
    if (lines?.length === 0) return "";
    return lines.join("\r\n") + "\r\n";
  }

  private _stringToUTF16(str: string): Uint8Array {
    const buffer = new ArrayBuffer(str.length * 2);
    const view = new Uint16Array(buffer);
    for (let i = 0; i < str.length; i++) {
      view[i] = str.charCodeAt(i);
    }
    return new Uint8Array(buffer);
  }

  private _escapeForRegExp(input: string): string {
    return input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  private _ensureCsvExtension(fileName: string): string {
    return /\.csv$/i.test(fileName) ? fileName : `${fileName}.csv`;
  }
}
